import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { animeList, categories } from '@/mocks/anime';
import type { AnimeItem } from '@/mocks/anime';
import Breadcrumb from '@/components/feature/Breadcrumb';

function AnimeCard({ anime }: { anime: AnimeItem }) {
  return (
    <Link
      to={`/anime/${anime.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
        <img
          src={anime.coverImage}
          alt={`${anime.title} - 51动漫网在线观看`}
          title={`${anime.title} - 51动漫在线观看免费全集`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {anime.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">热门</span>
          )}
          {anime.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">新番</span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 bg-foreground-900/70 text-white text-[10px] font-medium rounded-full whitespace-nowrap">{anime.rating}</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-900/80 to-transparent p-3">
          <span className="text-white text-[10px] font-medium">
            {anime.status === '连载中' ? `更新至${anime.episodes}话` : `共${anime.episodes}话`}
          </span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">{anime.title}</h3>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[10px] px-1.5 py-0.5 bg-secondary-100 text-secondary-800 rounded whitespace-nowrap">{anime.category}</span>
        <span className="text-xs text-foreground-500">{anime.views}</span>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('rating');
  const [statusFilter, setStatusFilter] = useState<'全部' | '连载中' | '已完结'>('全部');

  const currentCategory = categories.includes(type as typeof categories[number]) ? type! : '日漫';

  useEffect(() => {
    setSortBy('rating');
    setStatusFilter('全部');
  }, [type]);

  const filteredAnimes = animeList
    .filter((a) => a.category === currentCategory)
    .filter((a) => statusFilter === '全部' || a.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'views') return parseFloat(b.views.replace('亿', '')) - parseFloat(a.views.replace('亿', ''));
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  const categoryInfo: Record<string, { desc: string; icon: string }> = {
    '日漫': { desc: '经典日系动漫，热血冒险与感动并存，涵盖少年、校园、奇幻等各种题材。', icon: 'ri-sun-line' },
    '韩漫': { desc: '韩系精品漫画，画风精美剧情在线，条漫下拉式阅读体验极佳。', icon: 'ri-moon-line' },
    '国漫': { desc: '国风动漫崛起，修仙玄幻精彩纷呈，展现中华文化独特魅力。', icon: 'ri-fire-line' },
  };

  const info = categoryInfo[currentCategory];

  const metaTitle = `51动漫${currentCategory}在线观看 - 51动漫网${currentCategory}免费全集 | 下拉式追更`;
  const metaDescription = `51动漫网提供${currentCategory}高清免费在线观看服务，海量${currentCategory}全集免费追更，支持下拉式阅读，${currentCategory}爱好者追漫首选。`;
  const metaKeywords = `51动漫网,51动漫${currentCategory},51动漫在线观看,51动漫免费观看,${currentCategory},动漫追更,下拉式阅读`;

  return (
    <div className="pt-20 pb-14">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: metaTitle,
            description: metaDescription,
            url: `https://www.51dongman.com/category/${currentCategory}`,
            isPartOf: { '@type': 'WebSite', name: '51动漫网', url: 'https://www.51dongman.com/' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com/' },
              { '@type': 'ListItem', position: 2, name: currentCategory, item: `https://www.51dongman.com/category/${currentCategory}` },
            ],
          }),
        }}
      />

      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={`https://www.51dongman.com/category/${currentCategory}`} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={`https://www.51dongman.com/category/${currentCategory}`} />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: currentCategory }]} />

        <div className="bg-background-50 rounded-2xl p-6 md:p-10 mb-10 border border-background-200/70">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-50 flex-shrink-0">
              <i className={`text-3xl text-primary-500 ${info.icon}`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{currentCategory}专区</h1>
              <p className="text-sm text-foreground-500 mt-2 leading-relaxed max-w-2xl">{info.desc}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500 whitespace-nowrap">分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat}
                to={`/category/${cat}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat === currentCategory
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                {cat}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            {[
              { key: 'rating' as const, label: '评分' },
              { key: 'views' as const, label: '人气' },
              { key: 'updated' as const, label: '更新' },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortBy(opt.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-sm text-foreground-500 whitespace-nowrap">状态：</span>
          {(['全部', '连载中', '已完结'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                statusFilter === s
                  ? 'bg-secondary-500 text-white'
                  : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {filteredAnimes.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6">
            {filteredAnimes.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">暂无符合条件的动漫，换个筛选条件试试吧～</p>
          </div>
        )}
      </div>
    </div>
  );
}